const PLANS = {
  free: 'free',
  basic: 'basic',
  premium: 'premium',
  pro: 'pro',
  // `team` is self-serve Pro for multiple seats (SCI-2596). Same entitlement
  // tier as Pro — it's a billing/packaging distinction, not a feature one.
  team: 'team',
  organizational: 'organizational'
}

// premium is treated as equivalent to basic (grandfathered)
// team sits at the Pro tier so every `userIsAtLeast(user, PLANS.pro)` gate
// passes for team members without any per-call changes.
// organizational is intentionally omitted — it's handled via organizationLicense presence, not PLAN_LEVELS.
const PLAN_LEVELS = {
  [PLANS.free]: 0,
  [PLANS.basic]: 1,
  [PLANS.premium]: 1,
  [PLANS.pro]: 2,
  [PLANS.team]: 2
}

// B2B (org-licensed) users should always be on Pro or higher — floor lower tiers up to Pro.
// `organizational` is preserved as-is since it represents the highest B2B tier.
function ensureB2BPlan(plan) {
  if (plan === PLANS.organizational) {
    return plan
  }
  const level = PLAN_LEVELS[plan]
  if (level === undefined || level < PLAN_LEVELS[PLANS.pro]) {
    return PLANS.pro
  }
  return plan
}

const SUBSCRIPTION_STATUS = {
  inactive: 'inactive',
  active: 'active',
  trialing: 'trialing',
  paymentFailed: 'payment_failed',
  paused: 'paused'
}

const STRIPE_SUBSCRIPTION_STATUS = {
  trialing: 'trialing',
  active: 'active',

  // Can happen after a failed payment or during payment grace period
  incomplete: 'incomplete',

  // After a failed payment
  past_due: 'past_due',

  // Non-trial expires, where retries don't occur, perhaps within the expired retry
  // window of another subscription (trial)
  incomplete_expired: 'incomplete_expired',

  // a. Subscription was superceded by another
  // b. What happens when past-due retries run out
  canceled: 'canceled',

  // We don't use this. Instead, failed retries lead to canceled
  unpaid: 'unpaid',

  // We don't use this either. We support use of the pause_collection attribute but
  // that's different/unrelated to this status
  paused: 'paused'
}

// These indicate a user has an account
// sometimes they will have an account from the past
// and are just signing in which is ok.
const SIGNUP_ACTIONS = {
  CREATED_ACCOUNT: 'Created an Account',

  LOGGED_IN_LOCAL: 'Logged in',

  LOGGED_IN_FACEBOOK: 'Logged in - Facebook',
  SIGNED_UP_FACEBOOK: 'Signed up - Facebook',

  LOGGED_IN_GOOGLE: 'Logged in - Google',
  SIGNED_UP_GOOGLE: 'Signed up - Google',

  LOGGED_IN_SAML_SSO: 'Logged in - Saml SSO',
  SIGNED_UP_SAML_SSO: 'Signed up - Saml SSO',

  LOGGED_IN_AZURE: 'Logged in - Azure',
  SIGNED_UP_AZURE: 'Signed up - Azure',

  LOGGED_IN_OPEN_ATHENS: 'Logged in - OpenAthens',
  SIGNED_UP_OPEN_ATHENS: 'Signed up - OpenAthens'
}

// Max seats on a self-serve team (SCI-2345). Shared by the FE seat selectors
// and the server-side quantity clamp so the cap is defined once. Larger teams
// go through sales (Enterprise).
const MAX_SELF_SERVE_SEATS = 20

// Min seats on a self-serve team (SCI-4102). The Team plan includes 8 seats; the
// underlying Stripe price is $50/seat, and a $150/mo forever coupon (see
// stripe.teamSeatCouponID) nets the 8-seat base to $250/mo, with each seat beyond
// 8 adding $50. Teams scale up from 8 via Stripe checkout's adjustable_quantity
// (or the in-app seat-update endpoint). Shared by the FE seat selectors and the
// seat-update endpoint so the floor is defined once.
const MIN_SELF_SERVE_SEATS = 8

const LICENSE_STATUS = {
  active: 'LICENSE_ACTIVE',
  inactive: 'INACTIVE',
  trialing: 'TRIALING'
}

const LICENSE_AUTH_TYPE = {
  email: 'EMAIL_AUTHENTICATION',
  ip: 'IP_AUTHENTICATION',
  ipAndEmail: 'IP_AND_EMAIL_AUTHENTICATION'
}

// Max org logo upload size. Admins are asked for SVG or 1000px+ rasters, so the
// cap has to leave room for one. Shared by the multer limit and the upload
// form's client-side warning so the two can't drift.
const MAX_ORG_LOGO_BYTES = 2 * 1024 * 1024

const BLOCKED_PATHS_FOR_EXTERNALS = ['/admin', '/api/checkout', '/stripe']

const SENSITIVE_PARAMS = ['token', 'session_id', 'api_key', 'auth', 'password', 'secret']

module.exports = {
  PLANS,
  PLAN_LEVELS,
  ensureB2BPlan,
  SUBSCRIPTION_STATUS,
  STRIPE_SUBSCRIPTION_STATUS,
  SIGNUP_ACTIONS,
  MAX_SELF_SERVE_SEATS,
  MIN_SELF_SERVE_SEATS,
  LICENSE_STATUS,
  LICENSE_AUTH_TYPE,
  MAX_ORG_LOGO_BYTES,
  BLOCKED_PATHS_FOR_EXTERNALS,
  SENSITIVE_PARAMS
}
